import { createSelector } from 'create-selector'
import { createBundle } from 'core/bundler'
import {
  EXPLANATIONS_FETCH_REQUESTED,
  EXPLANATIONS_FETCH_STARTED,
  EXPLANATIONS_FETCH_SUCCEEDED,
  EXPLANATIONS_FETCH_FAILED,
  EXPLANATIONS_CLEARED,
  EXPLANATIONS_OUTDATED,
  EXPLANATIONS_EXPIRED,
  USER_LOGGED_OUT,
} from 'core/actiontypes'

const name = 'explanations' // e.g notification, trainings, users
const uCaseName = name.charAt(0).toUpperCase() + name.slice(1)
const defaultConfig = {
  stale: 300000, // five minutes
  retry: 60000, // fifteen seconds,
  maxRetryCount: 3,
  expireAfter: Infinity,
  persist: false,
  // checkIfOnline: true,
}

const initialState = []

const initialRequestState = {
  id: null,
  data: null,
  errorTimes: [],
  error: null,
  lastSuccess: null,
  isOutdated: false,
  isLoading: false,
  isExpired: false,
  failedPermanently: false,
}

const SELECTORS = {
  DATA: 'selectExplanations',
  IS_STALE: 'selectExplanationsIsStale',
  IS_EXPIRED: 'selectExplanationsIsExpired',
  IS_LOADING: 'selectExplanationsRequestIsLoading',
  IS_WAITING: 'selectExplanationsIsWaitingToRetry',
  LAST_ERROR: 'selectExplanationsRequestLastError',
  LAST_SUCCESS: 'selectExplanationsRequestLastSuccess',
  FAILED_PERMANENTLY: 'selectExplanationsRequestFailedPermanently',
}

export default config => {
  // eslint-disable-next-line no-param-reassign
  config = { ...defaultConfig, ...config }
  const markDataAsOutdated = id => ({ type: EXPLANATIONS_OUTDATED, payload: id })
  const clearData = id => ({ type: EXPLANATIONS_CLEARED, payload: id })
  const expireData = id => ({ type: EXPLANATIONS_EXPIRED, payload: id })
  const requestData = id => ({ type: EXPLANATIONS_FETCH_REQUESTED, payload: id })
  const fetchData =
    id =>
    (dispatch, { api }) => {
      dispatch({ type: EXPLANATIONS_FETCH_STARTED, payload: id })
      return api(`/scorings/${id}/explain`).then(
        data => {
          dispatch({
            type: EXPLANATIONS_FETCH_SUCCEEDED,
            payload: {
              id,
              data,
            },
          })
        },
        error => {
          dispatch({ type: EXPLANATIONS_FETCH_FAILED, error, payload: id })
        }
      )
    }
  const requestModelExplanation =
    (dataset, brain, objective, row) =>
    (dispatch, { api }) => {
      api(`/scorings/${dataset}/explain`, {
        method: 'POST',
        body: {
          scored_dataset_id: dataset,
          requests: {
            [brain]: {
              [objective]: (row === 0 || row) && [Number(row)],
            },
          },
        },
      }).then(data => {
        dispatch({
          type: EXPLANATIONS_FETCH_SUCCEEDED,
          payload: { id: dataset, data },
          merge: true,
        })
      })
    }

  const requestSelector = (state, id) =>
    id ? state[name].find(e => e.id === id) || null : state[name]

  const dataSelector = (state, key) => {
    const request = requestSelector(state, key)
    const data = request?.data || null
    if (key) return data
    return request
  }

  const isLoadingSelector = (state, key) => {
    const isLoading = requestSelector(state, key)?.isLoading
    return isLoading !== undefined ? isLoading : null
  }

  const isStaleSelector = (state, key) => {
    const { appTime } = state
    const request = requestSelector(state, key)
    const { lastSuccess, isOutdated } = request
    return isOutdated || appTime - lastSuccess > config.stale
  }

  const isExpiredSelector = (state, key) => {
    const isExpired = requestSelector(state, key)?.isExpired
    return isExpired !== undefined ? isExpired : null
  }

  const lastSuccessSelector = (state, key) => {
    const lastSuccess = requestSelector(state, key)?.lastSuccess
    return lastSuccess !== undefined ? lastSuccess : null
  }

  const lastErrorSelector = (state, key) => {
    const request = requestSelector(state, key)
    const { error, errorTimes } = request || {}
    return error ? errorTimes.slice(-1)[0] : null
  }

  const isWaitingToRetrySelector = (state, key) => {
    const { appTime } = state
    const request = requestSelector(state, key)
    const { error, errorTimes } = request || {}
    return error ? appTime - (errorTimes.slice(-1)[0] || null) > config.retry : null
  }

  // const failedPermanentlySelector = (state, key) => {
  //   const request = requestSelector(state, key)
  //   const { error, errorTimes } = request
  //   return error ? errorTimes.length > config.maxRetryCount : null
  // }

  const failedPermanentlySelector = (state, key) => {
    const failedPermanently = requestSelector(state, key)?.failedPermanently
    return failedPermanently !== undefined ? failedPermanently : null
  }

  const selectors = {
    [SELECTORS.DATA]: dataSelector,
    [SELECTORS.IS_LOADING]: isLoadingSelector,
    [SELECTORS.IS_STALE]: isStaleSelector,
    [SELECTORS.IS_WAITING]: isWaitingToRetrySelector,
    [SELECTORS.IS_EXPIRED]: isExpiredSelector,
    [SELECTORS.LAST_SUCCESS]: lastSuccessSelector,
    [SELECTORS.LAST_ERROR]: lastErrorSelector,
    [SELECTORS.FAILED_PERMANENTLY]: failedPermanentlySelector,
    reactMissingExplanations: createSelector(
      'selectUser',
      'selectAppTime',
      dataSelector,
      (user, appTime, requests) => {
        if (!user || !requests.length) return null
        const missing = requests
          .filter(e => !(e.data && !e.isOutdated)) // missing or outdated
          .filter(e => !e.isLoading) // isLoading
          .filter(e => e.errorTimes.length <= config.maxRetryCount) // failedPermanently
          .filter(e => appTime - (e.errorTimes.slice(-1)[0] || null) > config.retry) // waitingToRetry
          .filter(e => e.isOutdated || appTime - e.lastSuccess > config.stale) // isStale

        if (!missing.length) return null

        return (dispatch, { api }) => {
          const batch = []
          missing.forEach(request => {
            const { id } = request
            batch.push({ type: EXPLANATIONS_FETCH_STARTED, payload: id })
            api(`/scorings/${id}/explain`).then(
              data => {
                dispatch({
                  type: EXPLANATIONS_FETCH_SUCCEEDED,
                  payload: {
                    id,
                    data,
                  },
                })
              },
              error => {
                dispatch({ type: EXPLANATIONS_FETCH_FAILED, error, payload: id })
              }
            )
          })
          dispatch({ type: 'BATCH_ACTIONS', actions: batch })
        }
      }
    ),
  }

  if (config.expireAfter !== Infinity) {
    selectors[`reactExpire${uCaseName}`] = createSelector(
      lastSuccessSelector,
      'selectAppTime',
      (time, appTime) => {
        if (!time) {
          return null
        }
        if (appTime - time > config.expireAfter) {
          return expireData()
        }
        return null
      }
    )
  }

  return createBundle({
    name,
    reducer: (state = initialState, { type, payload, error, merge }) => {
      if (type === EXPLANATIONS_FETCH_REQUESTED) {
        const requestState = state.find(request => request.id === payload) || null
        if (!requestState) return [...state, { ...initialRequestState, id: payload }]
        return [
          ...state.filter(e => e !== requestState),
          {
            ...requestState,
            isOutdated: true,
          },
        ]
      }
      if (type === EXPLANATIONS_FETCH_STARTED) {
        const requestState = state.find(request => request.id === payload) || null
        return [
          ...state.filter(e => e !== requestState),
          { ...requestState, isLoading: true },
        ]
      }
      if (type === EXPLANATIONS_FETCH_SUCCEEDED) {
        const { id, data } = payload
        const requestState = state.find(request => request.id === id) || null
        const updatedData = merge ? [...requestState.data, ...data] : data
        return [
          ...state.filter(e => e !== requestState),
          {
            ...requestState,
            id,
            isLoading: false,
            data: updatedData,
            lastSuccess: Date.now(),
            errorTimes: [],
            error: null,
            failedPermanently: false,
            isOutdated: false,
            isExpired: false,
          },
        ]
      }
      if (type === EXPLANATIONS_FETCH_FAILED) {
        const errorMessage = (error && error.message) || error
        const requestState = state.find(request => request.id === payload) || null
        return [
          ...state.filter(e => e !== requestState),
          {
            ...requestState,
            isLoading: false,
            errorTimes: [...requestState.errorTimes, Date.now()],
            error: errorMessage,
            failedPermanently: !!(error && error.permanent),
          },
        ]
      }
      if (type === EXPLANATIONS_CLEARED || type === USER_LOGGED_OUT) {
        if (!payload) return initialState
        return [...state.filter(training => training.id !== payload)]
      }
      if (type === EXPLANATIONS_EXPIRED) {
        return state.map(request => {
          if (!payload)
            return {
              ...initialRequestState,
              id: request.id,
              isExpired: true,
              errorTimes: request.errorTimes,
              error: request.error,
            }
          if (request.id === payload)
            return {
              ...initialRequestState,
              id: request.id,
              isExpired: true,
              errorTimes: request.errorTimes,
              error: request.error,
            }
          return request
        })
      }
      if (type === EXPLANATIONS_OUTDATED) {
        return state.map(request => {
          if (!payload) return { ...request, isOutdated: true }
          if (request.id === payload) return { ...request, isOutdated: true }
          return request
        })
      }
      return state
    },
    selectors,
    actions: {
      requestModelExplanation,
      [`fetch${uCaseName}`]: fetchData,
      [`mark${uCaseName}AsOutdated`]: markDataAsOutdated,
      [`clear${uCaseName}`]: clearData,
      [`expire${uCaseName}`]: expireData,
      [`register${uCaseName}Request`]: requestData,
    },
    init: store => {
      // eslint-disable-next-line no-shadow
      store.serverEvent('EXPLANATION_FINISHED', ({ data }, store) => {
        store.dispatch(markDataAsOutdated(data.scored_dataset_id))
      })
      // eslint-disable-next-line no-shadow
      store.serverEvent('EXPLANATION_FAILED', ({ data }, store) => {
        store.dispatch(markDataAsOutdated(data.scored_dataset_id))
      })
    },
    args: null,
    middleware: null,
    persist: config.persist
      ? [
          EXPLANATIONS_FETCH_SUCCEEDED,
          EXPLANATIONS_EXPIRED,
          EXPLANATIONS_OUTDATED,
          EXPLANATIONS_CLEARED,
        ]
      : null,
  })
}
